/**
 * @file utils.ts
 *
 * Shared utility functions used across the App Suite component library:
 * stylesheet helpers, dev-only logging, URL helpers, device detection,
 * DOM dispatch, and the registerListeners / unregisterListeners event helpers.
 */

import { fullAppSuiteRootURL, isProduction, localDev, MIN_WIDTH, NO_MOBILE } from "../data/constants.ts";

export function stylesheetFromString(str:string){
  const stylesheet = new CSSStyleSheet();
  stylesheet.replaceSync(str);
  return stylesheet;
}

export async function styleSheetFromStringAsync(str:string){
  const stylesheet = new CSSStyleSheet();
  await stylesheet.replace(str);
  return stylesheet;
}

/**
 * Utility functions for development logging with caller information
 * These functions only log in development mode and include file and line information
 */

/**
 * Creates a stack trace to extract caller information
 * @returns Object containing file information
 */
const getCallerInfo = (): { file: string } => {
  const error = new Error();
  const stack = error.stack?.split('\n') ?? [];
  
  // Find the first line that's not from this file
  const callerLine = stack.find(line => 
    line.includes('at ') && 
    !line.includes('util.ts') && 
    !line.includes('getCallerInfo')
  );
  
  if (!callerLine) {
    return { file: 'unknown' };
  }
  
  // Extract file information
  const match = callerLine.match(/at (?:.*? \()?(.+):(\d+):(\d+)/);
  if (!match) {
    return { file: 'unknown' };
  }
  
  const filePath = match[1];
  
  // Extract just the filename from the path and remove query parameters
  const fileName = filePath.split('/').pop()?.split('?')[0] ?? filePath;
  
  return { file: fileName };
};

/**
 * Logs a message in development mode with caller information
 * @param args - Arguments to log
 */
export const devlog = (...args: any[]) => {
  // @ts-ignore
  if (localDev || window?.verbose===true) {
    // const { file } = getCallerInfo();
    console.log( ...args);
    // @ts-ignore
    window.devlogTraces&&console.trace();
  }
};

/**
 * Logs a warning in development mode with caller information
 * @param args - Arguments to log
 */
export const devwarn = (...args: any[]) => {
  if (import.meta.env.DEV || import.meta.env.BUILD_DEV) {
    const { file } = getCallerInfo();
    console.warn(`[${file}]\n`, ...args);
  }
};


/**
 * Logs an error in development mode with caller information
 * @param args - Arguments to log
 */
export const deverror = (...args: any[]) => {
  if (import.meta.env.DEV || import.meta.env.BUILD_DEV) {
    const { file } = getCallerInfo();
    console.error(`[${file}]\n`, ...args);
  }
};


export function assetUrl(path: string): string {
  const stripped = path.startsWith('/') ? path.slice(1) : path;
  return `${fullAppSuiteRootURL}/${stripped}`;
}

export function redirTo(path:string){
  let strippedPath = path
  if(path.startsWith("/")){
    strippedPath = strippedPath.slice(1)
  }
  window.location.href = `${fullAppSuiteRootURL}/${strippedPath}`;
}

/**
 * Navigate by clicking an anchor element instead of assigning
 * `window.location.href`. In the GrabCAD embedded webview a scripted location
 * assignment doesn't reach the host's URL whitelist, but an anchor click does, so
 * the navigation is allowed through. Use this for user-initiated "leave the app"
 * navigations that must also work inside the webview.
 *
 * The anchor goes into `document.body` for the click rather than staying
 * detached: the host only recognises a link when the click reaches the document
 * from a real light-DOM anchor. A detached anchor (or one inside a shadow root,
 * where the click is retargeted to the shadow host on the way up) never gives it
 * one, which is how "nothing happens" redirects came about. It's removed again
 * straight after.
 *
 * For a new tab, call it from a real click handler (a user gesture) — a popup
 * opened outside one gets blocked by normal browsers.
 */
export function navigateViaAnchor(href: string, newTab = false): void {
  const a = document.createElement('a');
  a.href = href;
  if (newTab) { a.target = '_blank'; a.rel = 'noopener noreferrer'; }
  a.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;';
  a.setAttribute('aria-hidden', 'true');
  document.body.appendChild(a);
  try { a.click(); } finally { a.remove(); }
}

/** Open a link in a new window from inside a shadow root. Same light-DOM
 *  anchor trick as navigateViaAnchor — `window.open` does nothing in GrabCAD
 *  Print's embedded webview, and an anchor inside a shadow root never reaches
 *  the host as a link. Call it from a real click handler (a user gesture), or
 *  normal browsers block the popup. */
export function openViaLightDomAnchor(href: string): void {
  navigateViaAnchor(href, true);
}

export function detectDeviceInfo(){

  const nav:Navigator = window.navigator
  var hasTouchScreen = false;
  var dims = [window.innerWidth,window.innerHeight]
  var UA = nav.userAgent;
  if ("maxTouchPoints" in navigator) {
      hasTouchScreen = nav.maxTouchPoints > 0;
  } else if ("msMaxTouchPoints" in navigator) {
      //@ts-ignore
      hasTouchScreen = nav.msMaxTouchPoints > 0;
  } else {
      var mQ = window.matchMedia && matchMedia("(pointer:coarse)");
      if (mQ && mQ.media === "(pointer:coarse)") {
          hasTouchScreen = !!mQ.matches;
      } else if ('orientation' in window) {
          hasTouchScreen = true; // deprecated, but good fallback
      } else {
          // Only as a last resort, fall back to user agent sniffing
          hasTouchScreen = (
              /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
              /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
          );
      }
  }

  if (hasTouchScreen) {
      // Do something here. 
      devlog("Has Touchscreen.",dims)
      if(NO_MOBILE && dims[0] < MIN_WIDTH){
      }
    }
  return {hasTouchScreen,UA}

}
export function UAContainsMobileKeywords(){
  const UA = window.navigator.userAgent
  return /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
          /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
}


interface Vec3DArr extends Array<number>{
  x:number,
  y:number,
  z:number,
}
export const vec3d =(x:number,y:number,z:number)=>{
  const newVec = [x,y,z]
  const vecProto = {
    get x(){return this[0]},
    get y(){return this[1]},
    get z(){return this[2]}
  } 
  Object.assign(newVec,vecProto)
  //@ts-ignore
  return newVec as Vec3DArr
}

// @ts-ignore
window.testVec = vec3d(1,2,3)

export function emptyStrIfNot(condition,string){
  return condition===false || condition===undefined || condition===null
  ?''
  :string
}

/**
 * Add all entries in `listeners` to `target` via addEventListener.
 * Pass the same map to unregisterListeners to clean up.
 */
export function registerListeners(
  target: EventTarget,
  listeners: Record<string, EventListenerOrEventListenerObject>,
): void {
  for (const [event, handler] of Object.entries(listeners)) {
    target.addEventListener(event, handler);
  }
}

/**
 * Remove all entries in `listeners` from `target` via removeEventListener.
 */
export function unregisterListeners(
  target: EventTarget,
  listeners: Record<string, EventListenerOrEventListenerObject>,
): void {
  for (const [event, handler] of Object.entries(listeners)) {
    target.removeEventListener(event, handler);
  }
}